import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import ConfigurableTable, { TableColumn, FilterConfig, ColumnFilter } from '../common/ConfigurableTable';
import { useDebouncedCallback } from '../../hooks/useDebounced';
import EnhancedSearchInput from '../common/EnhancedSearchInput';

interface Customer {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  customerType: string;
  deviceModels: string[];
  deviceSerialNumbers: string[];
  ticketCount: number;
  lastTicketDate: string | null;
  isRegistered: boolean;
  createdAt: string | null;
}

interface CustomerListProps {
  onCustomerSelect: (customer: Customer) => void;
  onCustomerEdit?: (customer: Customer) => void;
}

const CustomerList: React.FC<CustomerListProps> = ({ onCustomerSelect, onCustomerEdit }) => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Preferences state
  const [savedPreferences, setSavedPreferences] = useState<{
    itemsPerPage: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    searchTerm: string;
    countryFilter: string;
    customerTypeFilter: string;
  }>({
    itemsPerPage: 50,
    sortBy: 'name',
    sortOrder: 'asc',
    searchTerm: '',
    countryFilter: '',
    customerTypeFilter: ''
  });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; customer: Customer | null }>({
    show: false,
    customer: null
  });
  const [deleting, setDeleting] = useState(false);

  // Available filter options
  const countries = useMemo(() => {
    const countrySet = new Set(customers.map(c => c.country).filter(Boolean));
    return Array.from(countrySet).sort();
  }, [customers]);

  const customerTypes = useMemo(() => {
    const typeSet = new Set(customers.map(c => c.customerType).filter(Boolean));
    return Array.from(typeSet).sort();
  }, [customers]);

  // Load user preferences from localStorage on component mount
  useEffect(() => {
    const loadPreferences = (): {
      itemsPerPage: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      searchTerm: string;
      countryFilter: string;
      customerTypeFilter: string;
    } => {
      const storageKey = 'customer-list-preferences';
      const defaultPrefs: {
        itemsPerPage: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        searchTerm: string;
        countryFilter: string;
        customerTypeFilter: string;
      } = {
        itemsPerPage: 50,
        sortBy: 'name',
        sortOrder: 'asc',
        searchTerm: '',
        countryFilter: '',
        customerTypeFilter: ''
      };

      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          const sortOrder: 'asc' | 'desc' = parsed.sortOrder === 'desc' ? 'desc' : 'asc'; // Ensure valid sort order
          return {
            itemsPerPage: parsed.itemsPerPage || defaultPrefs.itemsPerPage,
            sortBy: parsed.sortBy || defaultPrefs.sortBy,
            sortOrder,
            searchTerm: parsed.searchTerm || defaultPrefs.searchTerm,
            countryFilter: parsed.countryFilter || defaultPrefs.countryFilter,
            customerTypeFilter: parsed.customerTypeFilter || defaultPrefs.customerTypeFilter,
          };
        }
      } catch (error) {
        console.error('Error loading customer preferences:', error);
      }
      return defaultPrefs;
    };

    const preferences = loadPreferences();
    
    // Apply loaded preferences
    setItemsPerPage(preferences.itemsPerPage);
    setSearchTerm(preferences.searchTerm);
    setCountryFilter(preferences.countryFilter);
    setCustomerTypeFilter(preferences.customerTypeFilter);
    
    setSavedPreferences(preferences);
    setPreferencesLoaded(true);
  }, []);

  // Save user preferences to localStorage
  const savePreferences = useCallback((preferences: any) => {
    try {
      const storageKey = 'customer-list-preferences';
      localStorage.setItem(storageKey, JSON.stringify(preferences));
      console.log('Customer preferences saved to localStorage');
    } catch (error) {
      console.error('Error saving customer preferences:', error);
    }
  }, []);

  // Debounced function to save preferences to localStorage
  const savePreferencesDebounced = useDebouncedCallback(
    (preferences: any) => {
      savePreferences(preferences);
    },
    1000,
    [savePreferences]
  );

  // Stable search handler to prevent re-renders and focus loss
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Save preferences when they change (debounced to avoid constant saving during typing)
  useEffect(() => {
    if (!preferencesLoaded) return;

    const preferences = {
      itemsPerPage,
      sortBy: savedPreferences.sortBy,
      sortOrder: savedPreferences.sortOrder,
      searchTerm,
      countryFilter,
      customerTypeFilter
    };

    // Only save if preferences actually changed
    const hasChanged = JSON.stringify(preferences) !== JSON.stringify({
      itemsPerPage: savedPreferences.itemsPerPage,
      sortBy: savedPreferences.sortBy,
      sortOrder: savedPreferences.sortOrder,
      searchTerm: savedPreferences.searchTerm,
      countryFilter: savedPreferences.countryFilter,
      customerTypeFilter: savedPreferences.customerTypeFilter
    });

    if (hasChanged) {
      savePreferencesDebounced(preferences);
    }
  }, [itemsPerPage, searchTerm, countryFilter, customerTypeFilter, preferencesLoaded, savePreferencesDebounced, savedPreferences]);

  const loadCustomers = useDebouncedCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        search: searchTerm || undefined,
        country: countryFilter || undefined,
        customerType: customerTypeFilter || undefined,
        sortBy: savedPreferences.sortBy,
        sortOrder: savedPreferences.sortOrder,
        page: currentPage,
        limit: itemsPerPage
      };

      const result = await apiService.getCustomers(filters);
      setCustomers(result.customers);
      setTotalItems(result.pagination.totalItems);
      setTotalPages(result.pagination.totalPages);
      setCurrentPage(result.pagination.currentPage);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, 800, []); // Increased delay and removed dependencies to make it stable

  useEffect(() => {
    if (preferencesLoaded) {
      loadCustomers();
    }
  }, [preferencesLoaded, searchTerm, countryFilter, customerTypeFilter, currentPage, itemsPerPage, loadCustomers]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };



  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      
      if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      }
    } catch {
      return 'Invalid Date';
    }
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setDeleteConfirm({ show: true, customer });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.customer) return;
    
    try {
      setDeleting(true);
      await apiService.deleteCustomer(deleteConfirm.customer.id);
      
      // Refresh the customer list
      await loadCustomers();
      
      setDeleteConfirm({ show: false, customer: null });
      
      // Show success message
      alert('Customer deleted successfully');
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      
      // Show specific error messages based on the error type
      let errorMessage = 'Failed to delete customer';
      
      if (error.message) {
        if (error.message.includes('INSUFFICIENT_PERMISSIONS')) {
          errorMessage = 'You do not have permission to delete customers. Admin access required.';
        } else if (error.message.includes('CUSTOMER_HAS_OPEN_TICKETS')) {
          errorMessage = 'Cannot delete customer: customer has unresolved tickets. Please resolve all tickets first.';
        } else if (error.message.includes('CUSTOMER_HAS_LINKED_DEVICES')) {
          errorMessage = 'Cannot delete customer: customer has linked devices. Please unlink devices first.';
        } else if (error.message.includes('RESOURCE_NOT_FOUND')) {
          errorMessage = 'Customer not found.';
        } else {
          errorMessage = error.message;
        }
      }
      
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Define table columns
  const columns: TableColumn<Customer>[] = [
    {
      id: 'name',
      label: 'Name',
      width: 200,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (customer) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {customer.name ? customer.name.charAt(0).toUpperCase() : customer.email.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-4 min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={customer.name || customer.email}>
              {customer.name || 'Unknown'}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      width: 250,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (customer) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={customer.email}>
          {customer.email}
        </div>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      width: 150,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (customer) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={customer.phone || '-'}>
          {customer.phone || '-'}
        </div>
      ),
    },
    {
      id: 'company',
      label: 'Company',
      width: 200,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (customer) => (
        customer.company ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 truncate max-w-full" title={customer.company}>
            {customer.company}
          </span>
        ) : <span className="text-gray-400">-</span>
      ),
    },
    {
      id: 'country',
      label: 'Country',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      render: (customer) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={customer.country || '-'}>
          {customer.country || '-'}
        </div>
      ),
    },
    {
      id: 'city',
      label: 'City',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (customer) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={customer.city || '-'}>
          {customer.city || '-'}
        </div>
      ),
    },
    {
      id: 'state',
      label: 'State',
      width: 100,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (customer) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={customer.state || '-'}>
          {customer.state || '-'}
        </div>
      ),
    },
    {
      id: 'customerType',
      label: 'Type',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      render: (customer) => (
        customer.customerType ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-700 text-blue-800 dark:text-blue-200">
            {customer.customerType}
          </span>
        ) : <span className="text-gray-400">-</span>
      ),
    },
    {
      id: 'deviceModels',
      label: 'Devices',
      width: 200,
      visible: true,
      resizable: true,
      sortable: false,
      filterable: true,
      filterType: 'text',
      render: (customer) => {
        const deviceModels = customer.deviceModels || [];
        const deviceSerialNumbers = customer.deviceSerialNumbers || [];
        
        return deviceModels.length > 0 ? (
          <div className="space-y-1">
            {deviceModels.slice(0, 2).map((model, index) => {
              const serial = deviceSerialNumbers[index] || 'No Serial';
              return (
                <div key={index} className="text-xs font-mono bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                  {model} | {serial}
                </div>
              );
            })}
            {deviceModels.length > 2 && (
              <div className="text-xs text-gray-500">+{deviceModels.length - 2} more</div>
            )}
          </div>
        ) : <span className="text-gray-400">None</span>;
      },
    },
    {
      id: 'ticketCount',
      label: 'Tickets',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (customer) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            customer.ticketCount > 0 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {customer.ticketCount}
          </span>
        </div>
      ),
    },
    {
      id: 'lastTicketDate',
      label: 'Last Ticket',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (customer) => (
        <div className="text-sm">
          <div className="text-gray-900 dark:text-white">
            {formatDate(customer.lastTicketDate)}
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">
            {formatRelativeTime(customer.lastTicketDate)}
          </div>
        </div>
      ),
    },
    {
      id: 'isRegistered',
      label: 'Registration',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Registered' },
        { value: 'false', label: 'Not Registered' }
      ],
      render: (customer) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
          customer.isRegistered
            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
        }`}>
          {customer.isRegistered ? 'Registered' : 'Not Registered'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (customer) => (
        <div className="text-sm text-gray-900 dark:text-white">
          {formatDate(customer.createdAt)}
        </div>
      ),
    },
  ];



  // Export function
  const handleExport = () => {
    const exportData = customers.map(customer => ({
      'Name': `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.name || customer.email,
      'Email': customer.email,
      'Phone': customer.phone || '-',
      'Company': customer.company || '-',
      'Country': customer.country || '-',
      'City': customer.city || '-',
      'State': customer.state || '-',
      'Customer Type': customer.customerType || 'Standard',
      'Device Models': (customer.deviceModels || []).join(', ') || 'None',
      'Device Serial Numbers': (customer.deviceSerialNumbers || []).join(', ') || 'None',
      'Ticket Count': customer.ticketCount || 0,
      'Last Ticket Date': formatDate(customer.lastTicketDate),
      'Status': customer.isRegistered ? 'Registered' : 'Anonymous',
      'Created Date': formatDate(customer.createdAt),
    }));

    const XLSX = require('xlsx');
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers');
    
    const filename = `customers-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error loading customers</h3>
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
            <button
              onClick={loadCustomers}
              className="mt-2 bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700 px-3 py-1 rounded text-sm text-red-800 dark:text-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <label htmlFor="customer-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Customers
            </label>
            <EnhancedSearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, email, phone, company, address, city, country, device models... (use &quot;quotes&quot; for exact matches)"
              showSearchTips={true}
              debounceMs={300}
            />
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <span>{totalItems} customers found</span>
          </div>
        </div>
      </div>

      <ConfigurableTable
        data={customers}
        columns={columns}
        loading={loading}
        storageKey="customers-table"
        title="Customers"
        searchTerm=""
        onSearchChange={undefined}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        onRowClick={onCustomerSelect}
        onExport={handleExport}
        exportFilename="customers"
        sortBy={savedPreferences.sortBy}
        sortOrder={savedPreferences.sortOrder}
        onSortChange={(newSortBy, newSortOrder) => {
          setSavedPreferences(prev => ({
            ...prev,
            sortBy: newSortBy,
            sortOrder: newSortOrder
          }));
          setCurrentPage(1); // Reset to first page when sorting changes
        }}
        actions={[
          {
            label: 'Edit',
            icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            ),
            onClick: (customer) => onCustomerEdit?.(customer),
            show: () => (user?.role === 'agent' && user?.permissions?.includes('customers.edit')) || user?.roleName === 'Admin',
            className: 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          },
          {
            label: 'Delete',
            icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            ),
            onClick: handleDeleteCustomer,
            show: () => (user?.role === 'agent' && user?.permissions?.includes('customers.delete')) || user?.roleName === 'Admin',
            className: 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
          }
        ]}
        emptyState={
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-.5a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No customers found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || columnFilters.length > 0 ? 'Try adjusting your search or filters.' : 'No customers to display.'}
            </p>
          </div>
        }
      />

      {/* Pagination Controls - Now positioned below the table */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-md">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {Math.min(((currentPage - 1) * itemsPerPage) + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} customers
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-1 text-sm border rounded-md ${
                    pageNumber === currentPage
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && deleteConfirm.customer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <svg className="w-16 h-16 text-red-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">Delete Customer</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to delete <strong>{deleteConfirm.customer.name || deleteConfirm.customer.email}</strong>? 
                  This will permanently remove the customer and all associated data.
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setDeleteConfirm({ show: false, customer: null })}
                  disabled={deleting}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList; 